import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { useSubmitCoiReview, type CoiDocument, type CoiReviewBody } from "./api";

// Phase 2b coi-upload-and-review FR-4 — customer review form. Loads
// extracted_fields as defaults; the customer corrects anything wrong,
// then submits. Dates are HTML date inputs; money is a plain integer
// input rendered in dollars (server stores cents).

function dollars(cents: number | null | undefined): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

function cents(dollarStr: string): number {
  const n = Number.parseFloat(dollarStr || "0");
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

interface Props {
  doc: CoiDocument;
  onSubmitted: () => void;
}

export function CoiReviewForm({ doc, onSubmitted }: Props): JSX.Element {
  const extracted = doc.extracted_fields ?? {};
  const submit = useSubmitCoiReview(doc.id);

  const [insurerName, setInsurerName] = useState<string>(String(extracted.insurer_name ?? ""));
  const [policyNumber, setPolicyNumber] = useState<string>(String(extracted.policy_number ?? ""));
  const [effective, setEffective] = useState<string>(String(extracted.effective_date ?? ""));
  const [expiry, setExpiry] = useState<string>(String(extracted.expiry_date ?? ""));
  const [glOcc, setGlOcc] = useState<string>(dollars(extracted.gl_each_occurrence_cents as number | null));
  const [glAgg, setGlAgg] = useState<string>(dollars(extracted.gl_aggregate_cents as number | null));
  const [autoCsl, setAutoCsl] = useState<string>(dollars(extracted.auto_combined_single_limit_cents as number | null));
  const [ai, setAi] = useState<boolean>(Boolean(extracted.additional_insured));
  const [lp, setLp] = useState<boolean>(Boolean(extracted.loss_payee));

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const body: CoiReviewBody = {
      insurer_name: insurerName.trim(),
      policy_number: policyNumber.trim(),
      effective_date: effective,
      expiry_date: expiry,
      gl_each_occurrence_cents: cents(glOcc),
      gl_aggregate_cents: cents(glAgg),
      additional_insured_on: ai,
      loss_payee_on: lp,
      ...(autoCsl ? { auto_combined_single_limit_cents: cents(autoCsl) } : {})
    };
    submit.mutate(body, { onSuccess: () => onSubmitted() });
  }

  return (
    <form className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr]" onSubmit={onSubmit}>
      <div className="md:col-span-2">
        <h2 className="text-lg font-semibold">Review your COI details</h2>
        <p className="text-sm text-zinc-600">
          We pre-filled what we could read. Please correct anything wrong before submitting.
        </p>
      </div>

      <div>
        <Label htmlFor="coi-insurer">Insurer name</Label>
        <Input id="coi-insurer" value={insurerName} onChange={(e) => setInsurerName(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="coi-policy">Policy number</Label>
        <Input id="coi-policy" value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="coi-eff">Effective date</Label>
        <Input id="coi-eff" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="coi-exp">Expiry date</Label>
        <Input id="coi-exp" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="coi-gl-occ">General liability — each occurrence (USD)</Label>
        <Input id="coi-gl-occ" inputMode="decimal" value={glOcc} onChange={(e) => setGlOcc(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="coi-gl-agg">General aggregate (USD)</Label>
        <Input id="coi-gl-agg" inputMode="decimal" value={glAgg} onChange={(e) => setGlAgg(e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="coi-auto">Auto liability — combined single limit (USD, optional)</Label>
        <Input id="coi-auto" inputMode="decimal" value={autoCsl} onChange={(e) => setAutoCsl(e.target.value)} />
      </div>
      <label className="flex items-center gap-2 text-sm text-brand-black">
        <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} />
        JDR named as additional insured
      </label>
      <label className="flex items-center gap-2 text-sm text-brand-black">
        <input type="checkbox" checked={lp} onChange={(e) => setLp(e.target.checked)} />
        JDR named as loss payee
      </label>

      <div className="md:col-span-2">
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? "Submitting…" : "Submit for review"}
        </Button>
      </div>
    </form>
  );
}
